import React, { Component } from "react";
import DateRangePicker from "react-daterange-picker";
import moment from "moment";
import { extendMoment } from "moment-range";
import "react-daterange-picker/dist/css/react-calendar.css";

import HelperClass from "../../config/helperClass";
import {
  addInvoice,
  getInvoiceBySalesOrder,
  getSingleSalesOrder,
} from "../../config/reseller_api_calls";
import NotificationToast from "../Notification/NotificationToast";
import {
  resolvePaymentTermDays,
  dueDateFromTerm,
  toDateInputValue,
} from "../../utils/helpers";

const extendedMoment = extendMoment(moment);
const auth = new HelperClass();
const MODAL_ID = "modalReusableCreateInvoice";

const fmt = (v) => {
  const n = Number(v) || 0;
  // Use up to 4 decimal places; trim trailing zeros but keep at least 2
  const fixed4 = n.toFixed(4);
  const trimmed = fixed4.replace(/(\.\d{2}\d*?)0+$/, "$1");
  const [intPart, decPart] = trimmed.split(".");
  return "$" + intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (decPart !== undefined ? "." + decPart : "");
};

const todayStr = () => toDateInputValue(new Date());

const MAX_FIXED = 999999.9999;

const toPositive = (val) => (val === "" || Number(val) >= 0 ? val : "");

const clampAdjustment = (val, type) => {
  if (val === "") return "";
  const n = Number(val);
  if (!Number.isFinite(n) || n < 0) return "";
  const max = type === "percentage" ? 100 : MAX_FIXED;
  return n > max ? String(max) : val;
};

const initialFormState = () => ({
  amount: "",
  issueDate: todayStr(),
  dueDate: "",
  paymentTermsDays: null,
  payment_terms: "",
  billingPeriod: "",
  billingPeriodRange: null,
  billingPeriodOpen: false,
  notes: "",
  taxType: "percentage",
  taxValue: "",
  taxQty: "",
  taxRate: "",
  shippingType: "fixed",
  shippingValue: "",
  shippingQty: "",
  shippingRate: "",
  submitting: false,
  succeeded: false,
  error: "",
  success: "",
  toastMsg: "",
  toastType: "success",
  alreadyInvoiced: 0,
  resolvedOrderTotal: null,
});

class CreateInvoiceModal extends Component {
  constructor(props) {
    super(props);
    this.state = { ...initialFormState(), prefilling: false };
    this.modalBodyRef = React.createRef();
  }

  scrollModalToTop = () => {
    if (this.modalBodyRef.current) {
      this.modalBodyRef.current.scrollTop = 0;
    }
  };

  componentDidMount() {
    if (this.props.show) this.prefill();
  }

  forceCleanupModal = () => {
    try {
      window.$("#" + MODAL_ID).modal("hide");
      window.$(".modal-backdrop").remove();
      window.$("body").removeClass("modal-open").css("padding-right", "");
    } catch (e) {}
  };

  componentDidUpdate(prevProps) {
    const wasShown = prevProps.show;
    const isShown = this.props.show;

    if (!wasShown && isShown) {
      this.prefill();
      window.$("#" + MODAL_ID).modal({ backdrop: "static", keyboard: false });
      window.$("#" + MODAL_ID).modal("show");
    } else if (wasShown && !isShown) {
      this.forceCleanupModal();
    } else if (
      isShown &&
      prevProps.salesOrder !== this.props.salesOrder
    ) {
      this.prefill();
    }
  }

  componentWillUnmount() {
    this.forceCleanupModal();
  }

  prefill = async () => {
    this.setState({ prefilling: true });
    const order = this.props.salesOrder || {};
    let orderTotal = Number(order.order_total || order.quote_total || 0);
    const prefillAlready = Number(order.already_invoiced || 0);

    const billingPeriodStr = order.billing_period || "";
    let billingPeriodRange = null;
    if (billingPeriodStr && billingPeriodStr.includes(" - ")) {
      const [s, e] = billingPeriodStr.split(" - ");
      const start = moment(s, "MM-DD-YYYY");
      const end = moment(e, "MM-DD-YYYY");
      if (start.isValid() && end.isValid()) {
        billingPeriodRange = extendedMoment.range(start, end);
      }
    }

    // Falls back to today only when the order has no issue date of its own —
    // the invoice should carry the order's issue date, not the date the user
    // happened to open this modal.
    let issueDate = todayStr();
    let payment_terms = order.payment_terms || "";

    // Always fetch the real order details when orderno is available so we get
    // the authoritative quote_total (the caller may have passed an invoice total
    // instead of the order total, e.g. when opening from the invoices list).
    let orderDueDate = "";
    if (order.orderno) {
      try {
        const resp = await getSingleSalesOrder(
          auth.getAccount(),
          auth.getToken(),
          order.orderno
        );
        const d = resp && resp.data;
        if (d && d.status === 200 && d.data && d.data[0]) {
          if (!payment_terms) payment_terms = d.data[0].payment_terms || "";
          const fetched = Number(d.data[0].quote_total || d.data[0].order_total || 0);
          if (fetched > 0) orderTotal = fetched;
          orderDueDate = String(d.data[0].dueDate || "").slice(0, 10);
          // DB `dateCreated`, exposed as issue_date. The `dateCreated` key on
          // this response carries `order_created_date` — the created date — so
          // it must not be used here.
          const orderIssueDate = String(d.data[0].issue_date || "").slice(0, 10);
          if (orderIssueDate) issueDate = orderIssueDate;
        }
      } catch (e) {}
    }

    // Prefer the order's own due date — it is already in the response fetched
    // above and reflects any manual correction. Only fall back to deriving it
    // from the payment term. The old code parsed the term text with a /^Net N$/
    // regex, so anything else ("Due on Receipt") left this blank.
    const rawDays =
      order.no_of_days != null
        ? Number(order.no_of_days)
        : resolvePaymentTermDays(payment_terms, []);
    let dueDate = orderDueDate || "";
    if (dueDate === "") {
      const d = dueDateFromTerm(issueDate, rawDays);
      if (d) dueDate = toDateInputValue(d);
    }

    this.setState({
      ...initialFormState(),
      resolvedOrderTotal: orderTotal,
      alreadyInvoiced: prefillAlready,
      amount: String(Math.max(orderTotal - prefillAlready, 0)),
      billingPeriod: billingPeriodStr,
      billingPeriodRange,
      payment_terms,
      paymentTermsDays: rawDays,
      // Must come after the initialFormState() spread, which resets this to
      // today — that is what kept overriding the order's issue date.
      issueDate,
      dueDate,
    });

    if (order.orderno) {
      try {
        const response = await getInvoiceBySalesOrder(
          auth.getAccount(),
          auth.getToken(),
          order.orderno
        );
        const payload = response && response.data;
        if (payload && payload.status === 200 && payload.data) {
          const rows = payload.data.rows || [];
          const computed = rows.reduce(
            (sum, inv) => sum + Number(inv.sub_total || 0),
            0
          );
          // Prefer the freshly-computed sub_total sum when invoices exist;
          // fall back to the parent-supplied value only when no rows are returned.
          const finalAlready = rows.length > 0 ? computed : prefillAlready;
          this.setState({
            alreadyInvoiced: finalAlready,
            amount: String(Math.max(orderTotal - finalAlready, 0)),
          });
        }
      } catch (e) {}
    }
    this.setState({ prefilling: false });
  };

  close = () => {
    if (this.state.submitting) return;
    this.props.onClose && this.props.onClose();
  };

  computeTotals = () => {
    const {
      amount: amountStr,
      taxType,
      taxValue,
      taxQty,
      taxRate,
      shippingType,
      shippingValue,
      shippingQty,
      shippingRate,
    } = this.state;

    const amount = Number(amountStr) || 0;
    const discount = 0;

    let tax = 0;
    if (taxType === "volume") {
      tax = (Number(taxQty) || 0) * (Number(taxRate) || 0);
    } else if (taxValue && Number(taxValue) > 0) {
      tax =
        taxType === "percentage"
          ? (amount * Number(taxValue)) / 100
          : Number(taxValue);
    }

    let shipping = 0;
    if (shippingType === "volume") {
      shipping = (Number(shippingQty) || 0) * (Number(shippingRate) || 0);
    } else if (shippingValue && Number(shippingValue) > 0) {
      shipping =
        shippingType === "percentage"
          ? (amount * Number(shippingValue)) / 100
          : Number(shippingValue);
    }

    return {
      amount,
      discount,
      tax,
      shipping,
      grand: amount - discount + tax + shipping,
    };
  };

  submit = async () => {
    const order = this.props.salesOrder || {};
    const { amount: amountStr, issueDate, dueDate } = this.state;

    if (!amountStr || Number(amountStr) <= 0) {
      this.setState({ error: "Invoice amount is required." }, this.scrollModalToTop);
      return;
    }
    if (!issueDate) {
      this.setState({ error: "Issue date is required." }, this.scrollModalToTop);
      return;
    }
    if (!dueDate) {
      this.setState({ error: "Due date is required." }, this.scrollModalToTop);
      return;
    }

    const { taxType, taxValue, taxQty, taxRate, shippingType, shippingValue, shippingQty, shippingRate } = this.state;
    if (taxType === "percentage" && Number(taxValue) > 100) {
      this.setState({ error: "Federal Tax percentage cannot exceed 100%." }, this.scrollModalToTop); return;
    }
    if (taxType !== "percentage" && Number(taxValue) > MAX_FIXED) {
      this.setState({ error: `Federal Tax amount cannot exceed ${fmt(MAX_FIXED)}.` }, this.scrollModalToTop); return;
    }
    if (taxType === "volume" && Number(taxRate) > MAX_FIXED) {
      this.setState({ error: `Federal Tax rate cannot exceed ${fmt(MAX_FIXED)}.` }, this.scrollModalToTop); return;
    }
    if (shippingType === "percentage" && Number(shippingValue) > 100) {
      this.setState({ error: "Shipping & Handling percentage cannot exceed 100%." }, this.scrollModalToTop); return;
    }
    if (shippingType !== "percentage" && Number(shippingValue) > MAX_FIXED) {
      this.setState({ error: `Shipping & Handling amount cannot exceed ${fmt(MAX_FIXED)}.` }, this.scrollModalToTop); return;
    }
    if (shippingType === "volume" && Number(shippingRate) > MAX_FIXED) {
      this.setState({ error: `Shipping & Handling rate cannot exceed ${fmt(MAX_FIXED)}.` }, this.scrollModalToTop); return;
    }

    const orderTotal = this.state.resolvedOrderTotal !== null
      ? this.state.resolvedOrderTotal
      : Number(order.order_total || order.quote_total || 0);
    const remaining = Math.max(orderTotal - (this.state.alreadyInvoiced || 0), 0);
    if (orderTotal > 0 && Number(amountStr) > remaining) {
      this.setState(
        { error: `Invoice amount cannot exceed the remaining balance of ${fmt(remaining)}.` },
        this.scrollModalToTop,
      );
      return;
    }

    const { amount, discount, tax, shipping, grand } = this.computeTotals();

    const invoicePayload = {
      orderno: order.orderno || "",
      reseller_accountno: order.reseller_accountno || auth.getAccount() || "",
      client_accountno: order.client_accountno || "",
      customer_name: order.customer_name || order.client_name || "",
      customer_email: order.customer_email || order.client_email || "",
      invoice_title: order.invoice_title || order.quote_title || "",
      currency: (order.currency || "USD").toString().toUpperCase(),
      invoice_date: issueDate,
      due_date: dueDate,
      sub_total: amount,
      discount_total: discount,
      tax_total: tax,
      shipping_total: shipping,
      grand_total: grand,
      notes: this.state.notes,
      payment_status: "Pending",
      payment_terms: this.state.payment_terms || "",
      invoice_type:
        orderTotal > 0 && grand < orderTotal ? "partial" : "standard",
      billing_period: this.state.billingPeriod || "",
    };
    console.log("[CreateInvoice] Submitting payload:", invoicePayload);

    this.setState({ submitting: true, error: "", success: "" });
    try {
      const response = await addInvoice(auth.getAccount(), auth.getToken(), invoicePayload);
      console.log("[CreateInvoice] Raw response:", response);
      const payload = response && response.data;
      console.log("[CreateInvoice] Response payload:", payload);
      if (
        payload &&
        (payload.status === 403 ||
          payload.errors === "authentication missing" ||
          payload.errors === "jwt expired")
      ) {
        console.warn("[CreateInvoice] Auth error — logging out.");
        auth.logout();
        return;
      }
      if (payload && payload.status === 200) {
        console.log("[CreateInvoice] Success:", payload);
        this.setState({ submitting: false, succeeded: true, success: "Invoice created successfully!", toastMsg: "Invoice created successfully!", toastType: "success" });

        // Let React unmount the modal cleanly (via show=false from parent) rather
        // than calling Bootstrap's modal("hide") first. Mixing Bootstrap DOM
        // manipulation with React's reconciler removal causes a removeChild crash.
        // componentDidUpdate handles Bootstrap cleanup after React removes the DOM.
        setTimeout(() => {
          this.props.onSuccess && this.props.onSuccess(payload);
          this.props.onClose && this.props.onClose();
        }, 1500);
      } else {
        console.error("[CreateInvoice] API returned error:", payload);
        this.setState(
          { submitting: false, error: (payload && payload.message) || "Failed to create invoice." },
          this.scrollModalToTop,
        );
      }
    } catch (e) {
      console.error("[CreateInvoice] Exception caught:", e);
      this.setState({ submitting: false, error: "Failed to create invoice." }, this.scrollModalToTop);
    }
  };

  render() {
    if (!this.props.show) return null;

    const order = this.props.salesOrder || {};
    const mode = this.props.mode || "standard";
    const currency = (order.currency || "USD").toString().toUpperCase();
    const orderTotal = this.state.resolvedOrderTotal !== null
      ? this.state.resolvedOrderTotal
      : Number(order.order_total || order.quote_total || 0);
    const alreadyInvoiced = this.state.alreadyInvoiced || 0;
    const remaining = Math.max(orderTotal - alreadyInvoiced, 0);
    const totals = this.computeTotals();

    const headerTitle =
      mode === "follow_up" ? "Create Follow-up Invoice" : "Create Invoice";

    return (
      <>
      <div className="modal fade zoom" tabIndex="-1" id={MODAL_ID}>
        <div className="modal-dialog modal-lg modal-dialog-top" role="document">
          <div
            className="modal-content"
            style={{ borderRadius: 10, overflow: "hidden" }}
          >
            <div
              className="modal-header"
              style={{ padding: "20px 24px 16px", borderBottom: "1px solid #e9ecef" }}
            >
              <div>
                <h5
                  className="modal-title"
                  style={{ fontWeight: 700, fontSize: 18, marginBottom: 3 }}
                >
                  {headerTitle}
                </h5>
                <p style={{ fontSize: 13, color: "#6B7280", margin: 0 }}>
                  {order.orderno ? `Order ${order.orderno}` : ""}
                  {order.client_name ? ` · ${order.client_name}` : ""}
                </p>
              </div>
              <a
                href="#"
                className="close"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  this.close();
                }}
                style={{ fontSize: 20 }}
              >
                <em className="icon ni ni-cross"></em>
              </a>
            </div>

            <div
              ref={this.modalBodyRef}
              className="modal-body"
              style={{ padding: "20px 24px", maxHeight: "72vh", overflowY: "auto", position: "relative" }}
            >
              {this.state.prefilling && (
                <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}>
                  <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }} />
                  <span style={{ fontSize: 13, color: "#6B6762" }}>Loading…</span>
                </div>
              )}
              <div
                style={{
                  border: "1px solid #e9ecef",
                  borderRadius: 8,
                  overflow: "hidden",
                  marginBottom: 20,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                }}
              >
                {[
                  {
                    label: "ORDER TOTAL",
                    value: fmt(orderTotal),
                    color: "#1F2937",
                  },
                  {
                    label: "ALREADY INVOICED",
                    value: fmt(alreadyInvoiced),
                    color: "#1A7A4A",
                  },
                  { label: "REMAINING", value: fmt(remaining), color: "#D97706" },
                ].map((item, idx) => (
                  <div
                    key={item.label}
                    style={{
                      padding: "14px 16px",
                      borderRight: idx < 2 ? "1px solid #e9ecef" : "none",
                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#9CA3AF",
                        letterSpacing: "0.09em",
                        textTransform: "uppercase",
                        marginBottom: 6,
                      }}
                    >
                      {item.label}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: item.color }}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>

              {this.state.error && (
                <div
                  className="alert alert-danger"
                  style={{ marginBottom: 16, fontSize: 13 }}
                >
                  <strong>Error: </strong>
                  {this.state.error}
                </div>
              )}
              {this.state.success && (
                <div
                  className="alert alert-success"
                  style={{ marginBottom: 16, fontSize: 13 }}
                >
                  {this.state.success}
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                  marginBottom: 16,
                }}
              >
                <div>
                  <label
                    className="form-label"
                    style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}
                  >
                    Invoice Amount ({currency}) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={this.state.amount}
                    onChange={(e) =>
                      this.setState({ amount: toPositive(e.target.value), error: "" })
                    }
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label
                    className="form-label"
                    style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}
                  >
                    Issue Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={this.state.issueDate}
                    onChange={(e) => {
                      const newIssueDate = e.target.value;
                      const update = { issueDate: newIssueDate, error: "" };
                      const d = newIssueDate
                        ? dueDateFromTerm(
                            newIssueDate,
                            this.state.paymentTermsDays
                          )
                        : null;
                      if (d) update.dueDate = toDateInputValue(d);
                      this.setState(update);
                    }}
                  />
                </div>
              </div>

              {this.state.payment_terms && (
                <div style={{ marginBottom: 20 }}>
                  <label
                    className="form-label"
                    style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}
                  >
                    Payment Terms
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    readOnly
                    value={this.state.payment_terms}
                    style={{ background: "#f5f6fa", cursor: "default" }}
                  />
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 16,
                  marginBottom: 20,
                }}
              >
                <div>
                  <label
                    className="form-label"
                    style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}
                  >
                    Due Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={this.state.dueDate}
                    onChange={(e) =>
                      this.setState({ dueDate: e.target.value, error: "" })
                    }
                    disabled={this.state.submitting}
                  />
                </div>
                <div style={{ position: "relative" }}>
                  <label
                    className="form-label"
                    style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}
                  >
                    Billing Period
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    readOnly
                    style={{ cursor: "pointer", background: "#fff" }}
                    value={this.state.billingPeriod || ""}
                    placeholder="Select billing period range"
                    onClick={() =>
                      this.setState({
                        billingPeriodOpen: !this.state.billingPeriodOpen,
                      })
                    }
                  />
                  {this.state.billingPeriod && (
                    <span
                      onClick={() =>
                        this.setState({
                          billingPeriod: "",
                          billingPeriodRange: null,
                          billingPeriodOpen: false,
                        })
                      }
                      style={{
                        position: "absolute",
                        right: 10,
                        top: "calc(50% + 6px)",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#9CA3AF",
                        fontSize: 16,
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </span>
                  )}
                  {this.state.billingPeriodOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        zIndex: 1200,
                        background: "#fff",
                        border: "1px solid #dbdfea",
                        borderRadius: 6,
                        boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
                        marginTop: 4,
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 8,
                          right: 10,
                          cursor: "pointer",
                          zIndex: 1201,
                          color: "#6B7280",
                          fontSize: 18,
                        }}
                        onClick={() => this.setState({ billingPeriodOpen: false })}
                      >
                        <em className="icon ni ni-cross"></em>
                      </div>
                      <DateRangePicker
                        value={this.state.billingPeriodRange}
                        onSelect={(range) => {
                          const start = range.start.format("MM-DD-YYYY");
                          const end = range.end.format("MM-DD-YYYY");
                          this.setState({
                            billingPeriodRange: range,
                            billingPeriod: `${start} - ${end}`,
                            billingPeriodOpen: false,
                          });
                        }}
                        singleDateRange={true}
                        numberOfCalendars={2}
                        showLegend={true}
                      />
                    </div>
                  )}
                </div>
              </div>

              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#9CA3AF",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  marginBottom: 12,
                }}
              >
                Billing Adjustments{" "}
                <span
                  style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}
                >
                  (from Billing Settings)
                </span>
              </p>

              {this.renderAdjustmentCard({
                title: "Federal Tax",
                hint: "(+) Added to subtotal",
                hintColor: "#1F2937",
                typeKey: "taxType",
                valueKey: "taxValue",
                qtyKey: "taxQty",
                rateKey: "taxRate",
                placeholder: "e.g. 5.5",
                allowFixed: true,
              })}
              {this.renderShippingCard()}

              <div
                style={{
                  background: "#EEF2FF",
                  borderRadius: 8,
                  padding: "14px 18px",
                  marginBottom: 20,
                }}
              >
                {[
                  { label: "Subtotal", val: totals.amount, sign: null },
                  { label: "Federal Tax", val: totals.tax, sign: "pos" },
                  { label: "Shipping & Handling", val: totals.shipping, sign: "pos" },
                ].map((r) => (
                  <div
                    key={r.label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 13,
                      color: "#4B5563",
                      marginBottom: 6,
                    }}
                  >
                    <span>
                      {r.label}{" "}
                      {r.sign === "neg" && <span style={{ color: "#1A7A4A" }}>(−)</span>}
                      {r.sign === "pos" && <span style={{ color: "#D97706" }}>(+)</span>}
                    </span>
                    <span>{fmt(r.val)}</span>
                  </div>
                ))}
                <div
                  style={{
                    borderTop: "1px solid #C7D2FE",
                    marginTop: 8,
                    paddingTop: 10,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: 14, color: "#1C5BDE" }}>
                    Grand Total
                  </span>
                  <span style={{ fontWeight: 700, fontSize: 16, color: "#1C5BDE" }}>
                    {fmt(totals.grand)}
                  </span>
                </div>
              </div>

              <div>
                <label
                  className="form-label"
                  style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}
                >
                  Invoice Notes
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Optional notes..."
                  value={this.state.notes}
                  onChange={(e) => this.setState({ notes: e.target.value })}
                  style={{ resize: "vertical", fontSize: 13 }}
                />
              </div>
            </div>

            <div
              className="modal-footer"
              style={{
                borderTop: "1px solid #e9ecef",
                padding: "14px 24px",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={this.close}
                disabled={this.state.submitting}
              >
                Cancel
              </button>
              {this.state.submitting || this.state.succeeded || this.state.prefilling ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled
                  style={{ background: "#101924", borderColor: "#101924" }}
                >
                  <span
                    className="spinner-border spinner-border-sm mr-1"
                    role="status"
                    aria-hidden="true"
                  />
                  {this.state.prefilling ? "Loading…" : "Creating..."}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={this.submit}
                  style={{
                    background: "#101924",
                    borderColor: "#101924",
                    paddingLeft: 20,
                    paddingRight: 20,
                  }}
                >
                  <em className="icon ni ni-check" style={{ marginRight: 6 }}></em>
                  Create Invoice
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <NotificationToast
        message={this.state.toastMsg}
        type={this.state.toastType}
        onDismiss={() => this.setState({ toastMsg: "" })}
      />
      </>
    );
  }

  renderAdjustmentCard = ({
    title,
    hint,
    hintColor,
    typeKey,
    valueKey,
    qtyKey,
    rateKey,
    placeholder,
  }) => {
    const type = this.state[typeKey];
    return (
      <div
        style={{
          border: "1px solid #e9ecef",
          borderRadius: 8,
          padding: "14px 16px",
          marginBottom: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 10,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{title}</div>
            <div style={{ fontSize: 12, color: hintColor, marginTop: 2 }}>{hint}</div>
          </div>
          <select
            className="form-control"
            style={{ width: 160, fontSize: 13 }}
            value={type}
            onChange={(e) => this.setState({ [typeKey]: e.target.value })}
          >
            <option value="percentage">% Percentage</option>
            <option value="fixed">$ Fixed Amount</option>
            {/* <option value="volume">Volume (qty × rate)</option> */}
          </select>
        </div>
        {type === "volume" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 11,
                  color: "#6B7280",
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                QTY
              </div>
              <input
                type="number"
                className="form-control"
                placeholder="0"
                value={this.state[qtyKey]}
                onChange={(e) => this.setState({ [qtyKey]: toPositive(e.target.value) })}
                min="0"
                step="1"
                style={{ textAlign: "right" }}
              />
            </div>
            <span style={{ paddingTop: 20, color: "#6B7280" }}>×</span>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 11,
                  color: "#6B7280",
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                RATE ($ PER UNIT)
              </div>
              <input
                type="number"
                className="form-control"
                placeholder="0.0000"
                value={this.state[rateKey]}
                onChange={(e) => this.setState({ [rateKey]: clampAdjustment(e.target.value, "fixed") })}
                min="0"
                max={MAX_FIXED}
                step="0.0001"
                style={{ textAlign: "right" }}
              />
            </div>
          </div>
        ) : (
          <div className="input-group">
            {type === "fixed" && (
              <div className="input-group-prepend">
                <span className="input-group-text" style={{ fontSize: 13 }}>
                  $
                </span>
              </div>
            )}
            <input
              type="number"
              className="form-control"
              placeholder={placeholder}
              value={this.state[valueKey]}
              onChange={(e) => this.setState({ [valueKey]: clampAdjustment(e.target.value, type) })}
              min="0"
              max={type === "percentage" ? 100 : MAX_FIXED}
              step="0.0001"
              style={{ textAlign: "right", color: "#1F2937" }}
            />
            <div className="input-group-append">
              <span
                className="input-group-text"
                style={{ fontSize: 13, color: "#9CA3AF" }}
              >
                {type === "percentage" ? "% of subtotal" : "fixed"}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  renderShippingCard = () => {
    const type = this.state.shippingType;
    return (
      <div
        style={{
          border: "1px solid #e9ecef",
          borderRadius: 8,
          padding: "14px 16px",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 10,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Shipping &amp; Handling</div>
            <div style={{ fontSize: 12, color: "#1F2937", marginTop: 2 }}>
              (+) Added to subtotal
            </div>
          </div>
          <select
            className="form-control"
            style={{ width: 160, fontSize: 13 }}
            value={type}
            onChange={(e) => this.setState({ shippingType: e.target.value })}
          >
            <option value="fixed">$ Fixed Amount</option>
            <option value="percentage">% Percentage</option>
            {/* <option value="volume">Volume (qty × rate)</option> */}
          </select>
        </div>
        {type === "volume" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 11,
                  color: "#6B7280",
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                QTY
              </div>
              <input
                type="number"
                className="form-control"
                placeholder="0"
                value={this.state.shippingQty}
                onChange={(e) => this.setState({ shippingQty: toPositive(e.target.value) })}
                min="0"
                step="1"
                style={{ textAlign: "right" }}
              />
            </div>
            <span style={{ paddingTop: 20, color: "#6B7280" }}>×</span>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 11,
                  color: "#6B7280",
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                RATE ($ PER UNIT)
              </div>
              <input
                type="number"
                className="form-control"
                placeholder="0.0000"
                value={this.state.shippingRate}
                onChange={(e) => this.setState({ shippingRate: clampAdjustment(e.target.value, "fixed") })}
                min="0"
                max={MAX_FIXED}
                step="0.0001"
                style={{ textAlign: "right" }}
              />
            </div>
          </div>
        ) : (
          <div className="input-group">
            {type === "fixed" && (
              <div className="input-group-prepend">
                <span className="input-group-text" style={{ fontSize: 13 }}>
                  $
                </span>
              </div>
            )}
            <input
              type="number"
              className="form-control"
              placeholder="0.0000"
              value={this.state.shippingValue}
              onChange={(e) => this.setState({ shippingValue: clampAdjustment(e.target.value, type) })}
              min="0"
              max={type === "percentage" ? 100 : MAX_FIXED}
              step="0.0001"
              style={{ textAlign: "right", color: "#1F2937" }}
            />
            <div className="input-group-append">
              <span
                className="input-group-text"
                style={{ fontSize: 13, color: "#9CA3AF" }}
              >
                {type === "percentage" ? "% of subtotal" : "fixed"}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };
}

export default CreateInvoiceModal;
